/* eslint-disable import/no-anonymous-default-export */
import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { ListSubheader, Button, Icon, Typography, Grid, TextField, LinearProgress } from '@mui/material';
import ScoreIcon from 'global/components/ScoreIcon';
import CheckpointForm from 'global/components/CheckpointForm';
import useStyles from './ChecklistScores.styles';
import clsx from 'clsx';
import { get, size, set, sortBy, unset, last, noop } from 'global/lib/lodash';
import { getId } from '../AssessmentScore';
import { useScreen } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useIntersect } from 'global/hooks/useIntersect';
import ModalGateway from 'global/components/ModalGateway';
import { getImageSource, uppercaseFirst } from 'global/utils';
import { Link } from 'react-router-dom';
import { getTaskStatus } from 'global/utils';
import { setTaskList } from 'redux/actions/view';
import { useDispatch } from 'react-redux';
import { DetailModal } from '../../task/TaskDetail';
import { CreateModal } from '../../task/CreateModal';
import { ImageList } from 'global/components/image';
import SnackAlerts from 'global/components/snack-alerts';
import { Loading } from 'global/styles';
import LibraryIcon from '@mui/icons-material/Book';
import TasksComments from './tasksComments';
import { useQueue } from 'global/hooks/useQueue';
import { MoreDrop } from 'global/components/more-drop';
import { useUser } from 'global/hooks/useUser';
import { Column, Row } from 'global/components/row';
import { Button as Btn } from 'global/components/Button';
import CheckPointInfoModal from 'global/components/CheckpointInfo';
import colors from 'global/styles/colors';
import { useTaskTypes } from '../../task/task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getAvailableTaskTypesForTier, getTaskTypeIdFromTask, getTaskTypeById } from '../../task/task-type-utils';
import * as moment from 'moment';
import { Text } from 'global/components/text';
import sendData from 'redux/actions/sendData';
import { cleanCopy, shouldUseSubEditView } from '../../../global/utils';
import { deleteSubScore, modifySubScore, SubScoreLauncher, getDisplayConfigFrom } from '../AssessmentScore/SubScore/SubScoreLauncher';
import { SubScoreCollapse } from '../AssessmentScore/SubScore/SubScoreCollapse';
import { AUTOTAG } from '../AssessmentScore';
import SubScoreModal, { SubScoreForm } from 'global/components/SubScoreModal';
import { Media } from '../../../global/components/Media';
import { findTaskForItem } from '../AssessmentScore/utils';

export default (props) => {
  const {
    pssvChecklist,
    assessment,
    setActiveEdit,
    activeEdit,
    updateScores,
    setCurrentAnchor,
    selectItem,
    isSelected,
    assessmentTasks,
    hasSelectedItems,
    showSelectBoxes,
    userCanEdit,
    canShowScores,
    scorecard,
    userCanLink,
    searchString,
    pssvSystem,
    updateAssessmentTasks,
    isCourse,
    isTemplate,
    printView,
  } = props;

  const [modal, setModal] = useState(false);
  const hasImage = get(pssvChecklist, 'checklist.image');
  const [isDirty, setIsDirty] = useState(false);
  const [stickyRef, stickyEntry] = useIntersect({ rootMargin: '-40px 0px -80% 0px' });

  const [alerts, setAlerts] = useState();
  const { queue: checklistScoreQueue, clearQueue, addToQueue: addToChecklistScoreQueue } = useQueue({ onUpdate: updateScores, setAlerts, system: pssvSystem });

  const calculatedScore = get(pssvChecklist, 'value');

  const loading = !!size(checklistScoreQueue);
  const classes = useStyles();

  useEffect(() => {
    const y = get(stickyEntry, 'isIntersecting', false);
    if (y) {
      setCurrentAnchor && setCurrentAnchor(get(props, 'pssvChecklist'));
    }
  }, [stickyEntry]);

  useEffect(() => {
    clearQueue();
  }, [activeEdit]);

  const onEditPress = useCallback(
    (e) => {
      if (activeEdit) {
        // User is closing the edit mode
        setActiveEdit();
        // Site redirects user/sidebar to the Checklist they were just editing
        setCurrentAnchor(pssvChecklist, true);
      } else {
        // User is entering edit mode, screen scrolls to edit section
        setActiveEdit({ ...props, scroll: true, scrollBy: -16 });
        setCurrentAnchor(pssvChecklist);
      }
    },
    [pssvChecklist]
  );

  const onImageClick = useCallback((img) => {
    setModal({ index: 0 });
  }, []);

  const id = getId(pssvChecklist);
  const currentSelect = isSelected(pssvChecklist);

  const useCompareScore = get(props, 'filter.assessment_status') !== 'active';
  const useCompare = useCompareScore && get(props, 'filter.useDiff');

  const ScoreIconProps = {
    ...(!activeEdit
      ? {
          scorecard: props.scorecard,
          useBlankCheckbox: printView ? false : showSelectBoxes,
          selected: !printView && showSelectBoxes && get(currentSelect, 'fullCount'),
          indeterminate: !printView && showSelectBoxes && currentSelect,
          onClick: printView ? undefined : () => selectItem(pssvChecklist),
          useCompare,
          compare: get(pssvChecklist, 'compare', null),
          displayValue: get(pssvChecklist, 'displayValue', null),
          canClick: false,
          filter: get(props, 'filter'),
        }
      : {}),

    score: useCompareScore && !useCompare ? get(pssvChecklist, 'compare.value') : calculatedScore,
    active: useCompareScore && !useCompare ? get(pssvChecklist, 'compare') : pssvChecklist,
  };

  const hasGenObs = !get(pssvChecklist, 'children.0.checkpointId');
  const pChildren = activeEdit && !hasGenObs ? [{}, ...get(pssvChecklist, 'children', [])] : get(pssvChecklist, 'children', []);
  const isCategory = get(props, 'filter.listGroup') === 'category';
  const isLocation = get(props, 'filter.listGroup') === 'location';
  const onlyLoc = get(props, 'filter.onlyLoc', false);
  const visibleDisplayValue = useCompareScore && !useCompare ? get(pssvChecklist, 'compare.displayValue') : get(pssvChecklist, 'displayValue');
  const checklistHasTasks = get(pssvChecklist, 'children', []).some((cp) => !!findTaskForItem(cp, assessmentTasks));
  const showChildren = visibleDisplayValue !== 'N/A' || checklistHasTasks || isCourse || activeEdit || searchString || hasGenObs || useCompare;

  const renderItem = (pssvCheckpoint, i) => {
    const isObs = !pssvCheckpoint.checkpointId;
    const hasChildren = isCategory && !!size(get(pssvCheckpoint, 'subscores'));
    const showCheckpoint = onlyLoc ? pssvCheckpoint.isSubScore || hasChildren : true;

    const items = (() => {
      const subScores = [];
      const autoSubs = [];
      let autoRender;

      if (!hasChildren) {
        return { subScores, autoSubs };
      }

      get(pssvCheckpoint, 'subscores', []).map((ss) => {
        if (ss.notes == AUTOTAG) {
          autoRender = {
            ...(autoRender || {}),
            ...pssvCheckpoint,
            ...ss,
            assessmentId: '',
            subscores: null,
            isSubScore: true,
            notes: '',
            criteria: get(autoRender, 'criteria') ? `${get(autoRender, 'criteria')}; ${get(ss, 'realm.name')}` : get(ss, 'realm.name'),
            name: `✔`,
            isAgg: true,
          };
          autoSubs.push(ss);
        } else {
          subScores.push({
            pssvParentCheckpoint: pssvCheckpoint,
            ...ss,
            assessmentId: '',
            subscores: null,
            isSubScore: true,
          });
        }
      });

      if (size(subScores) === 0 && size(autoSubs) === 1) {
        return { subScores, autoSubs: [], autoRender: undefined };
      }

      return { subScores, autoSubs, autoRender };
    })();

    if (isObs && i !== 0) {
      return null;
    }

    return (
      <Fragment key={isObs ? 'obs' : pssvCheckpoint.id}>
        {activeEdit ? (
          <EditScore
            {...props}
            assessment={assessment}
            scorecard={scorecard}
            queue={checklistScoreQueue}
            addToQueue={addToChecklistScoreQueue}
            pssvChecklist={pssvChecklist}
            pssvCheckpoint={pssvCheckpoint}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
          />
        ) : (
          <>
            {showCheckpoint && (
              <CheckpointScore
                {...props}
                searchString={searchString}
                updateAssessmentTasks={updateAssessmentTasks}
                scorecard={scorecard}
                canShowScores={canShowScores}
                assessmentTasks={assessmentTasks}
                hasSelectedItems={hasSelectedItems}
                showSelectBoxes={showSelectBoxes}
                isSelected={isSelected}
                selectItem={selectItem}
                pssvCheckpoint={{
                  ...pssvCheckpoint,
                  isSubScore: pssvCheckpoint.isSubScore && !hasChildren,
                }}
                pssvChecklist={pssvChecklist}
                assessment={assessment}
                isDirty={isDirty}
                setIsDirty={setIsDirty}
              />
            )}
            {hasChildren && (
              <div style={{ marginLeft: 16 }}>
                {!!items.autoRender && renderItem(items.autoRender, 0)}
                {items.subScores.map((ss, i) => renderItem(ss, i + size(items.subScores)))}
              </div>
            )}
          </>
        )}
      </Fragment>
    );
  };

  const renderItems = () => {
    const subEditView = isTemplate || isCourse ? false : shouldUseSubEditView(get(scorecard, 'config'));
    let lastSub;
    if (subEditView) {
      const f = pChildren.find((c) => c.isSubScore || !!size(c.subscores));
      lastSub = get(f, 'isSubScore') ? f : get(f, 'subscores.0', f);
      if (lastSub) {
        lastSub = cleanCopy(lastSub);
        set(lastSub, '_subscore.images', []);
        set(lastSub, '_subscore.notes', '');
        set(lastSub, '_subscore.isadd', true);

        unset(lastSub, '_subscore.id');
        if (isCategory) {
          unset(lastSub, '_subscore.realm');
        } else {
          set(lastSub, '_subscore.tag', null);
        }
      } else if (isCategory) {
        lastSub = {
          ...last(get(props, 'pssvChecklist.children', [{}])),
          supersetId: get(props, 'pssvChecklist.supersetId'),
          pssvChecklist: get(props, 'pssvChecklist.checklist'),
          _subscore: {},
        };
      }
    }

    const children = subEditView && activeEdit ? (isLocation ? pChildren.filter((p) => p.checkpointId) : pChildren) : pChildren;

    return (
      <>
        {subEditView && activeEdit && isLocation && !isTemplate && (
          <Row center>
            <SubScoreLauncher {...props} preserveRealmOnClose={true} style={{ marginLeft: 16, marginBottom: 8 }} />
          </Row>
        )}
        {children.map(renderItem)}
        {subEditView && activeEdit && isLocation && !isTemplate && (
          <Row center>
            <SubScoreLauncher {...props} preserveRealmOnClose={true} style={{ marginLeft: 16, marginBottom: 8 }} />
          </Row>
        )}
      </>
    );
  };

  return (
    <div id={id} data-testid={`checklist-${id}`}>
      <ListSubheader ref={printView ? undefined : stickyRef} disableSticky={!!printView} className={clsx(isCourse ? classes.checklistHeaderCourse : classes.checklistHeader, showChildren && classes.checklistHeaderBorder)} disableGutters={true}>
        {!isCourse && <ScoreIcon {...ScoreIconProps} />}
        {!isCourse && !useCompare && <h3 className={classes.cpScoreHeaderChild}>{get(ScoreIconProps, 'active.displayValue')}</h3>}
        <div className={classes.headerChildName}>
          <h3 title={pssvChecklist.name} className={classes.headerChildName}>
            {pssvChecklist.name}
          </h3>
          {get(pssvChecklist, 'criteria') && <span className={classes.headerCriteria}>{get(pssvChecklist, 'criteria')}</span>}
        </div>
        <div className={classes.grow} />
        {!printView && !activeEdit && !isCourse && userCanLink && (
          <Button component={Link} classes={{ root: classes.buttonRoot }} to={`/library/${scorecard.editionId}/document/${get(pssvChecklist, 'checklist.classificationId', '')}`}>
            <LibraryIcon fontSize="small" color="secondary" />
          </Button>
        )}
        {!printView && activeEdit && <PrevNext {...props} loading={loading} stickyEntry={stickyEntry} />}
        {!printView && (userCanEdit || activeEdit) && !props.isReview && (
          <Button className={activeEdit ? classes.headerBtn : null} color={activeEdit ? 'primary' : 'secondary'} onClick={() => onEditPress()}>
            <Icon fontSize="small">{activeEdit ? (loading ? Loading : 'close') : 'edit'}</Icon>
            <span className={classes.edit}>{activeEdit ? 'View' : 'Edit'}</span>
          </Button>
        )}
      </ListSubheader>
      {showChildren && (
        <>
          <Grid container spacing={0} justifyContent="space-around" direction={'row-reverse'}>
            <Grid item xs={12} className={classes.spacerTop} />
            {hasImage && (
              <Grid item xs={9} md={5} className={classes.checklistImage}>
                <Media {...get(pssvChecklist, 'checklist.image', {})} sourceType={['large', 'large-url', 'largeUrl']} onClick={onImageClick} />
              </Grid>
            )}
            <Grid item xs={12} md={hasImage ? 7 : 12} style={{ position: 'relative' }}>
              {renderItems()}
              {activeEdit && <PrevNext {...props} loading={loading} stickyEntry={stickyEntry} type={'fixed-bottom'} />}
            </Grid>
            <Grid item xs={12} className={classes.spacerBottom} />
          </Grid>

          {!printView && (
            <ModalGateway
              open={modal}
              onClose={() => setModal(false)}
              currentIndex={get(modal, 'index')}
              images={[hasImage]
                .filter((f) => f)
                .map((x, i) => ({
                  key: i,
                  src: getImageSource(get(x, 'large')),
                  caption: get(x, 'caption'),
                }))}
            />
          )}
        </>
      )}
      {!printView && <SnackAlerts alerts={alerts} setAlerts={setAlerts} />}
    </div>
  );
};

const PrevNext = (props) => {
  const { setActiveEdit, setCurrentAnchor, stickyEntry } = props;
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });

  const prev = get(props, 'prev');
  const next = get(props, 'next');

  const hasPrev = !!get(prev, 'id');
  const hasNext = !!get(next, 'id');

  const loading = props.loading || props.isUpdating;

  const prevPress = useCallback(
    (e) => {
      if (loading) {
        return;
      }
      setActiveEdit({
        ...props,
        pssvSystem: get(prev, '_system', get(props, 'pssvSystem')),
        pssvChecklist: prev,
        // We only scroll to top on prev-next if the sticky is near the top of the page,
        // because it's jarring for the user to be scrolled down when the sticky isn't sticky
        scroll: get(stickyEntry, 'isIntersecting', false),
        scrollBy: 48,
      });
      setCurrentAnchor(prev, true);
    },
    [stickyEntry, loading]
  );

  const nextPress = useCallback(
    (e) => {
      if (loading) {
        return;
      }
      setActiveEdit({
        ...props,
        pssvSystem: get(next, '_system', get(props, 'pssvSystem')),
        pssvChecklist: next,
        scroll: get(stickyEntry, 'isIntersecting', false),
        scrollBy: 48,
      });
      setCurrentAnchor(next, true);
    },
    [stickyEntry, loading]
  );

  if (props.type === 'fixed-bottom') {
    return (
      <div className={classes.fixedBottomNav}>
        {prev && (
          <Btn icon="keyboard_arrow_up" loading={loading} onClick={prevPress} disabled={!hasPrev}>
            PREV
          </Btn>
        )}
        {next && (
          <Btn icon="keyboard_arrow_down" loading={loading} disabled={!hasNext} onClick={nextPress}>
            NEXT
          </Btn>
        )}
      </div>
    );
  }

  return (
    <>
      {prev && (
        <Button color={'secondary'} onClick={prevPress} disabled={!hasPrev} size={isMobile ? 'small' : undefined} classes={{ root: classes.buttonRoot }}>
          <Icon>{loading ? Loading : 'keyboard_arrow_up'}</Icon>
          <span className={classes.edit}>PREV</span>
        </Button>
      )}
      {next && (
        <Button color={'secondary'} disabled={!hasNext} onClick={nextPress} size={isMobile ? 'small' : undefined} classes={{ root: classes.buttonRoot }}>
          <Icon>{loading ? Loading : 'keyboard_arrow_down'}</Icon>
          <span className={classes.edit}>NEXT</span>
        </Button>
      )}
    </>
  );
};

export const CommentContainer = (props) => {
  const { me } = useUser();
  const classes = useStyles();

  const { pssvCheckpoint, isCheckpointEditMode } = props;

  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState();
  const [comment, setComment] = useState({
    author: get(props, 'comment.authorName') || get(props, 'comment.authorEmail', ''),
    body: '',
    createdAt: undefined,
    images: null,
    meta: {},
    ...(props.comment || {}),
  });

  const onChange = (e) => {
    setComment({ ...comment, body: e.target.value });
  };

  const save = async () => {
    try {
      setLoading(true);
      const { scorecardScoreId } = pssvCheckpoint;
      await sendData({
        url: '/comments',
        method: 'post',
        data: {
          data: {
            type: 'comments',
            attributes: {
              user_id: me.id,
              ...comment,
              commentable_id: `${scorecardScoreId}`,
              commentable_type: 'scorecard_score',
            },
          },
        },
      });
      props.onSave && (await props.onSave(comment));
      props.onClose();
    } catch (err) {
      setAlerts({ message: `Error saving: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={clsx(classes.rComment, isCheckpointEditMode && classes.rCommentCheckpointEdit)}>
      <Column fullWidth className="column">
        <Row fullWidth className="textRow">
          <Text type="label_medium">{moment(comment.createdAt).format('L h:mma')}</Text>
          <Text type="label_medium">{comment.author}</Text>
          <div className="indicator" />
        </Row>
        <div className="textContainer">
          {props.comment && <Text type="label_medium">{comment.body}</Text>}
          {!props.comment && <TextField multiline size="small" rows={4} variant="outlined" value={comment.body} onChange={onChange} />}
        </div>
        {!props.comment && (
          <Row fullWidth className="btnRow">
            <Btn bgColor={colors.black} onClick={props.onClose}>
              CANCEL
            </Btn>
            <Btn loading={loading} onClick={save}>
              SAVE
            </Btn>
          </Row>
        )}
      </Column>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
};

export const CheckpointScore = (props) => {
  const { pssvCheckpoint, pssvChecklist, updateAssessmentTasks, assessment, selectItem, isSelected, assessmentTasks, showSelectBoxes, updateScores, scorecard, printView } = props;
  const [tasks, setTasks] = useState();
  const [taskModal, setTaskModal] = useState({ open: false });
  const [taskCreate, setTaskCreate] = useState({ open: false });
  const [checkpointInfo, setCheckpointInfo] = useState({});
  const [showComment, setShowComment] = useState(false);
  const { data: taskTypesData } = useTaskTypes();
  const assessmentRealmTier = get(assessment, 'data.realm.tier');
  const cpAvailableTypes = getAvailableTaskTypesForTier(getAvailableTaskTypes(taskTypesData), assessmentRealmTier);
  const [subModal, setSubModal] = useState({
    open: false,
    onClose: () => {
      setSubModal((sm) => ({ ...sm, open: false }));
      updateAssessmentTasks();
    },
  });
  const q = useQueue({ onUpdate: updateScores });
  const { queue: checkpointScoreQueue, addToQueue: addToCheckpointScoreQueue } = q;

  const isSubScore = pssvCheckpoint.isSubScore;
  const initialProcessedScore = {
    isEditMode: true,
    pssvSystem: props.pssvSystem,
    pssvChecklist: isSubScore ? get(pssvCheckpoint, 'pssvParentCheckpoint.checklist') : pssvChecklist,
    pssvCheckpoint: isSubScore ? pssvCheckpoint.pssvParentCheckpoint : pssvCheckpoint,
    ...(isSubScore && { pssvSubScore: pssvCheckpoint }),
  };
  const isAggregateSubScore = isSubScore && get(pssvCheckpoint, 'isAgg');
  const hasCriteria = get(pssvCheckpoint, 'criteria');
  const showNotes = get(props, 'showNotes', true);
  const hasNotes = get(pssvCheckpoint, 'notes');
  const hasImages = !!size(get(pssvCheckpoint, 'images'));
  const cpOpen = showNotes ? (hasNotes || hasImages ? true : false) : false;
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const { me } = useUser();

  const isGobs = get(pssvCheckpoint, 'depth') === 2;

  const onOpenTasks = () => {
    dispatch(setTaskList(tasks));
    setTaskModal({
      open: true,
      onClose: () => {
        setTaskModal({ open: false });
        updateAssessmentTasks();
      },
      taskId: get(tasks, '0.id'),
    });
  };

  useEffect(() => {
    if (!assessmentTasks) {
      return;
    }
    const hasTasks = assessmentTasks.filter((at) => {
      const isScoreMatch = (at?.scorecardScoreId === pssvCheckpoint?.scorecardScoreId) || (at?.scorecardScore?.lineageHash && at.scorecardScore.lineageHash === pssvCheckpoint.lineageHash);
      if (at.checkpointId === null && isGobs) {
        return isScoreMatch;
      }
      if (isSubScore && get(at, 'subScore.id') === pssvCheckpoint.id) {
        return true;
      }
      return isScoreMatch && at.checkpointId === pssvCheckpoint.checkpointId;
    });
    if (hasTasks) {
      setTasks(hasTasks);
    }
  }, [assessmentTasks]);

  const renderTasksCount = () => {
    const allTypes = getAvailableTaskTypes(taskTypesData);
    if (size(allTypes)) {
      // Group by type: "Action Plan (open); Issue (pending)"
      return tasks.map((t) => {
        const typeId = getTaskTypeIdFromTask(t);
        const taskType = getTaskTypeById(allTypes, typeId);
        const typeName = taskType ? taskType.name : 'Task';
        const { title } = getTaskStatus(t);
        const taskIsFromTargetAssessment = !((!!t.scorecardScoreId && t.scorecardScoreId === pssvCheckpoint.scorecardScoreId) || (pssvCheckpoint.isSubScore && t.subScore?.id === pssvCheckpoint.id))
        return `${taskIsFromTargetAssessment ? 'Target ' : ''}${typeName} (${title})`;
      }).join('; ');
    }
    // Fallback: group by status
    const counts = {
      open: 0,
      pending: 0,
      closed: 0,
    };
    tasks.map((t) => {
      const { title } = getTaskStatus(t);
      set(counts, title, get(counts, title, 0) + 1);
    });
    const order = ['open', 'pending', 'closed'];
    return order
      .filter((o) => !!get(counts, o))
      .map((o) => `${get(counts, o)} ${uppercaseFirst(o)} Task${get(counts, o) > 1 ? 's' : ''}`)
      .join(', ');
  };

  const task = get(tasks, '0');

  let score = (() => {
    if (isGobs) {
      return 'N/A';
    } else {
      return pssvCheckpoint.value;
    }
  })();

  const isCategory = get(props, 'filter.listGroup') === 'category';
  const isSubCat = isSubScore && isCategory;
  const isStaticScore = isCategory && !!size(pssvCheckpoint.subscores);

  const name = (() => {
    if (isGobs) {
      return 'General Observations';
    } else {
      if (isSubScore && !isAggregateSubScore) {
        return isSubCat ? get(pssvCheckpoint, 'realm.name', pssvCheckpoint.name) : `${get(pssvCheckpoint, 'checklist.name')}: ${get(pssvCheckpoint, 'name')}`;
      }
      return pssvCheckpoint.name;
    }
  })();

  const openTaskCreate = (p = {}) => {
    setTaskCreate({
      open: true,
      checkpoint: pssvCheckpoint,
      checklist: get(pssvCheckpoint, 'checklist', pssvChecklist),
      realmId: get(assessment, 'data.realmId'),
      realmTier: assessmentRealmTier,
      assignedTo: get(assessment, 'data.assignees', [me]),
      assessment: assessment.data,
      ...(size(cpRemainingTypes) === 1 ? { taskTypeId: get(cpRemainingTypes, '0.id') } : {}),
      onClose: async (created) => {
        if (created) {
          await updateAssessmentTasks();
        }
        setTaskCreate({ open: false });
      },
      ...p,
    });
  };

  // Filter out task types that already have a task for this checkpoint/score
  const existingTaskTypeIds = (tasks || []).map((t) => getTaskTypeIdFromTask(t)).filter(Boolean).map(String);
  const cpRemainingTypes = cpAvailableTypes.filter((tt) => !existingTaskTypeIds.includes(String(tt.id)));

  const options = [
    { title: 'Select', hide: isAggregateSubScore, onClick: () => selectItem(pssvCheckpoint) },
    {
      title: 'Pass',
      hide: !isSubScore || isAggregateSubScore || score === 100 || !props.userCanModify,
      onClick: async () => {
        const result = await modifySubScore({ ...get(pssvCheckpoint, '_subscore', {}), score: 100 });
        await updateScores(result);
      },
    },
    {
      title: 'Fail',
      hide: !isSubScore || isAggregateSubScore || score === 0 || !props.userCanModify,
      onClick: async () => {
        const result = await modifySubScore({ ...get(pssvCheckpoint, '_subscore', {}), score: 0 });
        await updateScores(result);
      },
    },
    {
      title: 'Delete Instance',
      hide: !isSubScore || isAggregateSubScore || !props.userCanModify,
      onClick: async () => {
        const result = await deleteSubScore({ ...get(pssvCheckpoint, '_subscore', {}), score: 0 });
        await updateScores(result);
      },
    },
    {
      title: 'Edit Instance',
      hide: !isSubScore || isAggregateSubScore || !props.userCanModify,
      onClick: async () => {
        setSubModal((sm) => ({
          ...sm,
          open: true,
          isDialog: true,
          initialProcessedScore: initialProcessedScore,
          realm: get(pssvCheckpoint, 'realm'),
        }));
      },
    },
    ...(size(cpRemainingTypes) > 1 ? cpRemainingTypes.map((tt) => ({
      title: `Create ${tt.name}`,
      hide: isStaticScore || isAggregateSubScore || props.isTemplate || get(assessment, 'data.meta.tasksEnabled') === false,
      onClick: () => {
        setTaskCreate({
          open: true,
          taskTypeId: tt.id,
          checkpoint: pssvCheckpoint,
          checklist: get(pssvCheckpoint, 'checklist', pssvChecklist),
          realmId: get(assessment, 'data.realmId'),
          realmTier: assessmentRealmTier,
          assignedTo: get(assessment, 'data.assignees', [me]),
          assessment: assessment.data,
          onClose: async (created) => {
            if (created) {
              await updateAssessmentTasks();
            }
            setTaskCreate({ open: false });
          },
        });
      },
    })) : [{
      title: size(cpRemainingTypes) === 1 ? `Create ${get(cpRemainingTypes, '0.name')}` : 'Create Task',
      hide: isStaticScore || isAggregateSubScore || props.isTemplate || get(assessment, 'data.meta.tasksEnabled') === false || (size(cpAvailableTypes) > 0 && size(cpRemainingTypes) === 0),
      onClick: () => {
        setTaskCreate({
          open: true,
          ...(size(cpRemainingTypes) === 1 ? { taskTypeId: get(cpRemainingTypes, '0.id') } : {}),
          checkpoint: pssvCheckpoint,
          checklist: get(pssvCheckpoint, 'checklist', pssvChecklist),
          realmId: get(assessment, 'data.realmId'),
          realmTier: assessmentRealmTier,
          assignedTo: get(assessment, 'data.assignees', [me]),
          assessment: assessment.data,
          onClose: async (created) => {
            if (created) {
              await updateAssessmentTasks();
            }
            setTaskCreate({ open: false });
          },
        });
      },
    }]),
    {
      title: 'Respond to Author',
      hide: get(assessment, 'data.status') !== 'review',
      onClick: () => {
        setTaskCreate({
          open: true,
          title: 'Respond to Author',
          checkpoint: pssvCheckpoint,
          checklist: pssvChecklist,
          realmId: get(assessment, 'data.realmId'),
          realmTier: assessmentRealmTier,
          assessment: assessment.data,
          assignedTo: get(assessment, 'data.authors', []),
          clientId: get(),
          onClose: async (created) => {
            if (created) {
              await updateAssessmentTasks();
            }
            setTaskCreate({ open: false });
          },
        });
      },
    },
    {
      title: 'Checkpoint Info',
      hide: isSubScore,
      onClick: () =>
        setCheckpointInfo({
          open: true,
          checklist: pssvChecklist,
          pssvCheckpoint: pssvCheckpoint,
          scorecard: scorecard,
          onClose: () => setCheckpointInfo({ open: false }),
        }),
    },
  ].filter((f) => !f.hide);

  const comments = get(
    get(props, 'assessment.data.scorecardScores', []).find((c) => c.id == get(pssvCheckpoint, 'scorecardScoreId')),
    'comments',
    []
  );

  const useCompareScore = get(props, 'filter.assessment_status') !== 'active';
  const useCompare = useCompareScore && get(props, 'filter.useDiff');
  const compare = get(pssvCheckpoint, 'compare', null);
  const showContent = get(props, 'showContent', true);
  const showDisplayScore = get(props, 'showDisplayScore', true) && !useCompare && !isGobs;
  const displayLabel = isSubScore
    ? (scorecard?.config?.subScoreValueLabel || []).find(
        (v) => v.value == pssvCheckpoint.value
      )?.label
    : pssvCheckpoint.displayValue;
  const showDrop = get(props, 'showDrop', true) && !props.isReview && !props.isCourse;
  const isTargetGoal = get(props, 'filter.assessment_status') == 'targetGoal';

  if (isTargetGoal) {
    score = get(pssvCheckpoint, 'target.value');
  }

  const scoreProps =
    isSubScore || isGobs
      ? { score }
      : {
          score: useCompareScore && !useCompare ? get(compare, 'value') : score,
          compare,
          useCompare,
          active: useCompareScore && !useCompare ? compare : pssvCheckpoint,
          openTaskCreate,
          taskTypeName: size(cpRemainingTypes) === 1 ? get(cpRemainingTypes, '0.name') : null,
        };

  return isGobs && !hasCriteria ? null : (
    <div className={classes.topic}>
      <div className={classes.rowTop}>
        <div className={classes.row}>
          {isGobs && useCompare ? (
            <div style={{ width: 8 }}></div>
          ) : (
            <ScoreIcon
              {...q}
              {...props}
              useBlankCheckbox={printView ? false : (isSubScore && isCategory ? false : showSelectBoxes)}
              selected={!printView && showSelectBoxes && isSelected(pssvCheckpoint)}
              onClick={printView ? undefined : () => selectItem && selectItem(pssvCheckpoint)}
              center
              isBw={false}
              disabled={isSubScore && isCategory}
              {...scoreProps}
              {...(printView ? { openTaskCreate: undefined } : {})}
            />
          )}
          {showDisplayScore && (
            <Typography variant="body2" className={classes.cpScoreHeaderChild}>
              {displayLabel}
            </Typography>
          )}
        </div>
        <Typography variant="body2" style={{ marginTop: '2px' }}>
          <strong>{name}</strong>
          &nbsp;
          {hasCriteria}
        </Typography>
        {!printView && (
          <div className={classes.moreIcon}>
            {props.isReview && get(assessment, 'data.status') !== 'completed' && (
              <Button onClick={() => setShowComment(!showComment)}>
                <Icon>add_comment</Icon>
              </Button>
            )}
            {showDrop && get(pssvCheckpoint, 'scorecardScoreId') && <MoreDrop options={options} />}
          </div>
        )}
      </div>
      {showContent && (
        <>
          {cpOpen && (
            <div className={classes.cpscoreHidden}>
              {!!hasNotes && <div className={classes.criteria}>{`Notes: ${hasNotes || ''}`}</div>}
              {!!hasImages && (
                <div className={printView ? clsx(classes.images, classes.imagesprint) : classes.images}>
                  <ImageList
                    style={classes.image}
                    {...(printView ? {
                      spaceBetween: 3,
                      showCaption: true,
                      srcOverride: { full: 'large', thumb: 'thumb' },
                    } : {
                      assessment: get(assessment, 'data'),
                      onClose: (st) => (st.hasEdits ? updateScores() : noop),
                      imageSize: 100,
                      spaceBetween: 3,
                      showCaption: true,
                      notAllowed: ['tag'],
                      collectionName: 'scorecard-score-images',
                      parentId: get(pssvCheckpoint, 'scorecardScoreId'),
                      childImagesIncludedInEdit: true,
                    })}
                    images={sortBy(pssvCheckpoint.images, 'orderColumn')}
                  />
                </div>
              )}
            </div>
          )}
          {task && (
            <div className={classes.row}>
              <span className={classes.cpScoreHeaderChild} />
              <Link className={classes.link} onClick={printView ? undefined : onOpenTasks} to={printView ? undefined : '#'}>
                {renderTasksCount()}
              </Link>
            </div>
          )}

          {task && <TasksComments tasks={tasks} />}

          {!!size(comments) && comments.map((com) => <CommentContainer comment={com} />)}
          {showComment && <CommentContainer pssvCheckpoint={pssvCheckpoint} onClose={() => setShowComment(false)} onSave={props.onCommentSave} />}

          {!printView && (
            <>
              <DetailModal {...taskModal} />
              <CreateModal {...taskCreate} />
              <SubScoreModal
                {...props}
                {...subModal}
                isDirty={props.isDirty}
                setIsDirty={props.setIsDirty}
                setNeedUpdate={true}
                addToQueue={addToCheckpointScoreQueue}
                queue={checkpointScoreQueue}
                isCourse={false}
                pssvTag={initialProcessedScore}
              />
              <CheckPointInfoModal {...checkpointInfo} />
            </>
          )}
        </>
      )}
    </div>
  );
};

const urlParams = {
  include: 'scorecardOptions',
};

export const EditScore = (props) => {
  return <AEditScore {...props} />;
};

const AEditScore = (props) => {
  const classes = useStyles();
  const { pssvCheckpoint, pssvChecklist, assessment, addToQueue, scorecard } = props;
  const checkpointId = get(pssvCheckpoint, 'checkpointId');
  const isObs = !checkpointId;
  const [state, setState] = useState({
    isSending: false,
    formState: {},
  });

  const [params] = useState({
    useEffect: true,
    waitFor: isObs ? null : undefined,
    params: urlParams,
    build: ['checkpoints'],
    transformData: (d) => get(d, '0'),
  });
  const { isView1, isView2, isView3, hasSubScores, showObservationGrid } = getDisplayConfigFrom(scorecard, props);

  const checkpointdata = useRetrieveData({ url: `/checkpoints/${checkpointId}` }, params);

  useEffect(() => {
    if (isObs) {
      checkpointdata.manuallySetData(pssvCheckpoint);
    }
  }, []);

  const { isSubScore } = pssvCheckpoint;

  const onFormReady = async (obj) => {
    try {
      if (obj.submits > 1) {
        //dont submit on first ready
        setState((s) => ({ ...s, isSending: true }));
        const result = await obj.submitSubScore();
        props.updateScores(result);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setState((s) => ({ ...s, isSending: false }));
    }
  };

  let SUBS = null;
  if (size(pssvCheckpoint.subscores)) {
    if (showObservationGrid) {
      SUBS = null;
    } else {
      SUBS = (
        <div style={isView1 || isView3 ? { marginLeft: 10, borderLeft: `4px solid ${colors.lightGreyBorder}` } : {}}>
          {pssvCheckpoint.subscores.map((subcheck, i) => (
            <AEditScore {...props} key={`${subcheck.id}_${i}`} pssvCheckpoint={subcheck} />
          ))}
        </div>
      );
    }
  }

  const isCompareModal = get(props, 'isCompareModal', false);

  const key = 'checkpoint-form-' + (isObs ? 'obs' : pssvCheckpoint.id);

  const SCORE = isSubScore ? (
    <SubScoreForm
      {...props}
      {...state}
      pssvTag={{
        pssvChecklist: pssvCheckpoint.pssvParentCheckpoint.checklist,
        pssvCheckpoint: pssvCheckpoint.pssvParentCheckpoint,
        pssvSubScore: pssvCheckpoint,
        isEditMode: true,
      }}
      canChangeTag={false}
      onFormReady={onFormReady}
    />
  ) : isView2 && !isObs ? null : (
    <CheckpointForm
      highlightCompare={get(props, 'pssvCheckpoint.target.value') ?? undefined}
      {...props}
      key={key}
      setNeedUpdate={props.setNeedUpdate}
      isCourse={props.isCourse}
      addToQueue={addToQueue}
      queue={props.queue}
      pssvCheckpoint={pssvCheckpoint}
      assessmentId={get(assessment, 'data.id')}
      pssvChecklist={pssvChecklist}
      checkpointdata={checkpointdata}
      loading={checkpointdata.loading}
      scorecard={scorecard}
      assessment={assessment}
      canEditInfo={isObs ? true : hasSubScores ? isView1 : undefined}
      renderObservations={
        showObservationGrid && !isCompareModal
          ? () => {
              return (
                <div className={classes.roomBtnContainer}>
                  {size(pssvCheckpoint.subscores) ? (
                    <SubScoreCollapse
                      {...props}
                      isDirty={props.isDirty}
                      setIsDirty={props.setIsDirty}
                      canEdit
                      pssvCheckpoint={{
                        ...pssvCheckpoint,
                      }}
                    />
                  ) : undefined}
                  {!props.isTemplate && (
                    <SubScoreLauncher
                      {...props}
                      realmDropProps={{ placeholder: 'ADD INSTANCE' }}
                      initialProcessedScore={{ pssvCheckpoint, pssvChecklist, pssvSystem: props.pssvSystem }}
                      style={{ marginLeft: 16, marginBottom: 8 }}
                      pssvCheckpoint={pssvCheckpoint}
                      activeEdit={true}
                      hasSubScores={true}
                    />
                  )}
                </div>
              );
            }
          : undefined
      }
    />
  );

  const blocked = state.isSending || (props.queue || []).find((q) => q.id === pssvCheckpoint.checkpointId);

  const renderLoader = () => {
    return <div className={classes.loadContainer}>{blocked && <LinearProgress />}</div>;
  };

  return pssvCheckpoint.error ? (
    <div>{`Error: ${get(pssvCheckpoint, 'error.message')}`}</div>
  ) : (
    <div className={classes.topicEdit} key={key}>
      {renderLoader()}
      {SCORE}
      {SUBS}
    </div>
  );
};
